import { API_BASE_URL } from './config';
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config';

type RequestOptions = RequestInit & {
  baseUrl?: string;
  preferSession?: boolean;
};

type ApiError = Error & {
  status?: number;
  url?: string;
};

const isApiError = (error: unknown): error is ApiError =>
  typeof error === 'object' && error !== null && 'status' in error;

export type AuthUser = {
  subject?: string;
  username?: string;
  email?: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  roles: string[];
  attributes?: { bio?: string[] } & Record<string, unknown>;
};

export type AuthResponse = {
  authenticated: boolean;
  user?: AuthUser | null;
  accessTokenExpiresAt?: number | null;
  refreshTokenExpiresAt?: number | null;
};

const STORAGE_TOKEN_KEYS = [
  'kc_token',
  'kcToken',
  'kc_access_token',
  'keycloak_token',
  'keycloakToken',
  'keycloak.access_token',
  'access_token',
  'accessToken',
  'token',
];

const GLOBAL_TOKEN_KEYS = [
  'kcToken',
  'keycloakToken',
  'accessToken',
  'authToken',
];

const SESSION_PRESENCE_KEY = 'kavithai.session.presence';

let inMemorySessionPresence = false;

const getSafeStorages = (): Storage[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const storages: Storage[] = [];

  try {
    if (window.localStorage) {
      storages.push(window.localStorage);
    }
  } catch (error) {
    // Access may throw in hardened environments – ignore
  }

  try {
    if (window.sessionStorage) {
      storages.push(window.sessionStorage);
    }
  } catch (error) {
    // Access may throw in hardened environments – ignore
  }

  return storages;
};

const readLikelySessionPresence = (): boolean => {
  if (inMemorySessionPresence) {
    return true;
  }

  for (const storage of getSafeStorages()) {
    try {
      if (storage.getItem(SESSION_PRESENCE_KEY) === '1') {
        inMemorySessionPresence = true;
        return true;
      }
    } catch (error) {
      // Ignore storage read failures
    }
  }

  return false;
};

const setLikelySessionPresence = (present: boolean): void => {
  inMemorySessionPresence = present;

  for (const storage of getSafeStorages()) {
    try {
      if (present) {
        storage.setItem(SESSION_PRESENCE_KEY, '1');
      } else {
        storage.removeItem(SESSION_PRESENCE_KEY);
      }
    } catch (error) {
      // Ignore storage write failures
    }
  }
};

const isProbablyJwt = (value: string | null | undefined): value is string => {
  if (!value) {
    return false;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }

  // Basic JWT shape check without decoding – three base64url sections
  const parts = trimmed.split('.');
  return (
    parts.length === 3 && parts.every(part => /^[A-Za-z0-9_-]+$/.test(part))
  );
};

const extractTokenCandidate = (
  value: string | null | undefined
): string | null => {
  if (!value) {
    return null;
  }

  if (isProbablyJwt(value)) {
    return value.trim();
  }

  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'string' && isProbablyJwt(parsed)) {
      return parsed;
    }

    if (parsed && typeof parsed === 'object') {
      const possibleKeys = ['access_token', 'accessToken', 'token', 'value'];
      for (const key of possibleKeys) {
        const candidate = (parsed as Record<string, unknown>)[key];
        if (typeof candidate === 'string' && isProbablyJwt(candidate)) {
          return candidate.trim();
        }
      }
    }
  } catch (error) {
    // Ignore JSON parsing issues – fall through to null
  }

  return null;
};

const readTokenFromStorage = (
  storage: Storage | undefined | null
): string | null => {
  if (!storage) {
    return null;
  }

  for (const key of STORAGE_TOKEN_KEYS) {
    try {
      const token = extractTokenCandidate(storage.getItem(key));
      if (token) {
        return token;
      }
    } catch (error) {
      // Some browsers (Safari private mode) may throw on access – ignore
    }
  }

  // Fall back to scanning all keys for any JWT-looking value that mentions Keycloak
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key) {
        continue;
      }

      if (!/token/i.test(key) && !/kc/i.test(key) && !/keycloak/i.test(key)) {
        continue;
      }

      const token = extractTokenCandidate(storage.getItem(key));
      if (token) {
        return token;
      }
    }
  } catch (error) {
    // Ignore enumeration failures (can happen in hardened environments)
  }

  return null;
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const segments = token.split('.');
  if (segments.length < 2) {
    return null;
  }

  const payload = segments[1];
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(
    normalized.length + ((4 - (normalized.length % 4)) % 4),
    '='
  );
  try {
    const decoder =
      typeof globalThis !== 'undefined' && typeof globalThis.atob === 'function'
        ? globalThis.atob
        : null;
    if (!decoder) {
      return null;
    }
    const decoded = decoder(padded);
    return JSON.parse(decoded);
  } catch (error) {
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    return true;
  }

  const exp = payload.exp;
  if (typeof exp !== 'number') {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return exp <= now;
};

const parseSessionCookieNames = (): string[] => {
  const fromEnv = import.meta.env.VITE_SESSION_COOKIE_NAMES;
  if (typeof fromEnv === 'string' && fromEnv.trim()) {
    return fromEnv
      .split(',')
      .map(name => name.trim())
      .filter(Boolean);
  }

  return ['hetzner_session', 'hetzner_session_dev'];
};

const SESSION_COOKIE_NAME_SET = new Set(
  parseSessionCookieNames().map(name => name.toLowerCase())
);

const hasLikelySessionCookie = (): boolean => {
  if (readLikelySessionPresence()) {
    return true;
  }

  if (typeof document === 'undefined' || !document.cookie) {
    return false;
  }

  return document.cookie.split(';').some(cookie => {
    const [rawName, rawValue] = cookie.split('=');
    if (!rawName || typeof rawValue === 'undefined') {
      return false;
    }

    const name = rawName.trim().toLowerCase();
    if (!SESSION_COOKIE_NAME_SET.has(name)) {
      return false;
    }

    return rawValue.trim().length > 0;
  });
};

const getStoredAccessToken = (): string | null => {
  const storages = getSafeStorages();

  for (const storage of storages) {
    const token = readTokenFromStorage(storage);
    if (token) {
      return token;
    }
  }

  // Check common globals injected by legacy auth flows
  if (typeof window !== 'undefined') {
    for (const key of GLOBAL_TOKEN_KEYS) {
      const candidate = (window as unknown as Record<string, unknown>)[key];
      if (typeof candidate === 'string' && isProbablyJwt(candidate)) {
        return candidate.trim();
      }
    }
  }

  for (const storage of storages) {
    try {
      for (let index = 0; index < storage.length; index += 1) {
        const key = storage.key(index);
        if (!key) {
          continue;
        }

        if (!/token/i.test(key) && !/kc/i.test(key) && !/keycloak/i.test(key)) {
          continue;
        }

        const token = extractTokenCandidate(storage.getItem(key));
        if (token) {
          return token;
        }
      }
    } catch (error) {
      // Ignore enumeration failures (can happen in hardened environments)
    }
  }

  return null;
};

async function request<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    baseUrl = API_BASE_URL,
    headers: initialHeaders,
    credentials,
    preferSession = false,
    ...rest
  } = options;
  const init: RequestInit = rest;
  const finalCredentials: RequestCredentials = credentials ?? 'include';

  const headers = new Headers(initialHeaders);
  if (!headers.has('Authorization') && !preferSession) {
    const token = getStoredAccessToken();
    if (token && !isTokenExpired(token) && !hasLikelySessionCookie()) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }
  if (
    !headers.has('Content-Type') &&
    init.body &&
    !(init.body instanceof FormData)
  ) {
    headers.set('Content-Type', 'application/json');
  }

  if (!preferSession) {
    maybeAttachAuthorization(headers, finalCredentials);
  }

  const url = `${baseUrl}${path}`;
  const usedAuthorizationHeader = headers.has('Authorization');
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: finalCredentials,
  });

  if (response.status === 401 && !usedAuthorizationHeader) {
    setLikelySessionPresence(false);
  }

  const contentType = response.headers.get('content-type') || '';
  const parseJson = async () => {
    if (contentType.includes('application/json')) {
      return response.json() as Promise<T>;
    }
    return response.text() as unknown as T;
  };

  if (!response.ok) {
    const payload = await parseJson().catch(() => null);
    let message: string | undefined;
    if (payload && typeof payload === 'object') {
      if (
        'message' in payload &&
        typeof (payload as any).message === 'string'
      ) {
        message = (payload as any).message;
      } else if (
        'detail' in payload &&
        typeof (payload as any).detail === 'string'
      ) {
        message = (payload as any).detail;
      } else if (
        'title' in payload &&
        typeof (payload as any).title === 'string'
      ) {
        message = (payload as any).title;
      }
    }

    if (!message) {
      message = `${response.status} ${response.statusText}`;
    }

    const error = Object.assign(new Error(message), {
      status: response.status,
      url,
    });

    throw error;
  }

  return parseJson();
}

export const api = {
  // Direct backend authentication
  login: async (identifier: string, password: string) => {
    // Backend expects 'username' or 'email', not 'identifier'
    // Check if identifier looks like an email
    const isEmail = identifier.includes('@');
    const payload = isEmail
      ? { email: identifier, password }
      : { username: identifier, password };

    const response = await request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    setLikelySessionPresence(Boolean(response.authenticated && response.user));
    return response;
  },
  forgotPassword: async (email?: string, username?: string) => {
    return await request<{ success: boolean; message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email, username }),
    });
  },
  logout: async () => {
    try {
      return await request<AuthResponse>('/auth/logout', { method: 'POST' });
    } finally {
      setLikelySessionPresence(false);
    }
  },
  me: async () => {
    const sessionResponse = await request<AuthResponse>('/auth/me', {
      preferSession: true,
    });

    if (sessionResponse.authenticated && sessionResponse.user) {
      setLikelySessionPresence(true);
      return sessionResponse;
    }

    setLikelySessionPresence(false);

    const token = getStoredAccessToken();
    if (token && !isTokenExpired(token)) {
      return request<AuthResponse>('/auth/me');
    }

    return sessionResponse;
  },
  register: async (payload: {
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    password: string;
  }) => {
    return request<{ success: boolean; message: string; userId?: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updateProfile: async (payload: {
    firstName: string;
    lastName: string;
    bio?: string;
  }) => {
    return request<void>('/auth/profile', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  updatePassword: async (payload: {
    currentPassword: string;
    newPassword: string;
  }) => {
    return request<void>('/auth/profile/password', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  listPoems: async (params?: { scope?: string; status?: string | string[] }) => {
    const qs = new URLSearchParams()
    if (params?.scope) {
      qs.set('scope', params.scope)
    }
    if (params?.status) {
      const statusValue = Array.isArray(params.status) ? params.status.join(',') : params.status
      if (statusValue) {
        qs.set('status', statusValue)
      }
    }
    const url = qs.toString() ? `/poems?${qs.toString()}` : '/poems'
    try {
      return await request(url);
    } catch (error) {
      if (
        API_BASE_URL === '/api' &&
        isApiError(error) &&
        (error.status === 401 || error.status === 403 || error.status === 404)
      ) {
        console.warn(
          `Primary poems endpoint (${API_BASE_URL}) responded with ${error.status}, retrying without base URL.`
        );
        return request(url, { baseUrl: '' });
      }

      throw error;
    }
  },
  getPoem: async (id: string) => {
    try {
      return await request(`/poems/${id}`);
    } catch (error) {
      if (
        API_BASE_URL === '/api' &&
        isApiError(error) &&
        (error.status === 401 || error.status === 403 || error.status === 404)
      ) {
        console.warn(
          `Primary poem detail endpoint (${API_BASE_URL}) responded with ${error.status}, retrying without base URL.`
        );
        return request(`/poems/${id}`, { baseUrl: '' });
      }

      throw error;
    }
  },
  listFavorites: async (type?: 'like' | 'bookmark' | 'all') => {
    const qs = type ? `?type=${encodeURIComponent(type)}` : '';
    return request(`/poems/favorites${qs}`);
  },
  togglePoemLike: (id: number) => request(`/poems/${id}/like`, { method: 'POST' }),
  togglePoemBookmark: (id: number) =>
    request(`/poems/${id}/bookmark`, { method: 'POST' }),
  recordPoemShare: (id: number) => request(`/poems/${id}/share`, { method: 'POST' }),
  recordPoemCopy: (id: number) => request(`/poems/${id}/copy`, { method: 'POST' }),
  recordPoemView: (id: number) => request(`/poems/${id}/view`, { method: 'POST' }),
  createPoem: (payload: {
    title: string;
    language: string;
    content: string;
    author?: string;
    tags?: string[];
    category?: string;
    published?: boolean;
    status?: string;
    description?: string;
    mood?: string;
    theme?: string;
    country?: string;
  }) => request('/poems', { method: 'POST', body: JSON.stringify(payload) }),
  updatePoem: (
    id: number,
    payload: {
      title: string;
      language: string;
      content: string;
      author?: string;
      tags?: string[];
      category?: string;
      published?: boolean;
      status?: string;
      description?: string;
      mood?: string;
      theme?: string;
      country?: string;
    }
  ) =>
    request(`/poems/${id}`, { method: 'PUT', body: JSON.stringify(payload) }),
  deletePoem: (id: number) => request(`/poems/${id}`, { method: 'DELETE' }),

  // Admin endpoints
  admin: {
    getPendingUsers: async () => {
      return await request<UserRepresentation[]>('/admin/users/pending');
    },
    approveUser: async (userId: string) => {
      return await request<{ message: string }>(`/admin/users/${userId}/approve`, {
        method: 'POST',
      });
    },
    rejectUser: async (userId: string) => {
      return await request<{ message: string }>(`/admin/users/${userId}/reject`, {
        method: 'POST',
      });
    },
  },
};

export type UserRepresentation = {
  id?: string;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  emailVerified?: boolean;
  createdTimestamp?: number;
};
